import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { checkPasswordStrength, PASSWORD_STRENGTH_LEVELS } from "@/lib/utils";

interface NewPasswordInputProps {
  value: string;
  onChange: (password: string) => void;
  confirmValue?: string;
  onConfirmChange?: (confirmPassword: string) => void;
  placeholder?: string;
  confirmPlaceholder?: string;
  className?: string;
  showConfirmation?: boolean;
}

export function NewPasswordInput({
  value,
  onChange,
  confirmValue = "",
  onConfirmChange,
  placeholder = "Enter your password",
  confirmPlaceholder = "Confirm your password",
  className = "",
  showConfirmation = false,
}: NewPasswordInputProps) {
  const [passwordStrength, setPasswordStrength] = useState<ReturnType<
    typeof checkPasswordStrength
  > | null>(null);

  const handlePasswordChange = (newPassword: string) => {
    onChange(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const getStrengthColor = (score: number) => {
    switch (score) {
      case 0:
        return "bg-red-500";
      case 1:
        return "bg-orange-500";
      case 2:
        return "bg-yellow-500";
      case 3:
        return "bg-green-500";
      case 4:
        return "bg-emerald-500";
      default:
        return "bg-gray-200";
    }
  };

  const passwordsMatch = value === confirmValue && value.length > 0;
  const showMismatchError = confirmValue.length > 0 && !passwordsMatch;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          value={value}
          type="password"
          onChange={(e) => handlePasswordChange(e.target.value)}
          placeholder={placeholder}
          className={`h-12 w-full ${className}`}
        />
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <Info className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[300px] p-4">
              <p className="mb-2 font-medium">Password Requirements:</p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>At least 8 characters long</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include numbers</li>
                <li>Include special characters</li>
                <li>Avoid common passwords and patterns</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {value && (
        <div className="space-y-2">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength?.score ?? 0)}`}
              style={{ width: `${((passwordStrength?.score ?? 0) + 1) * 20}%` }}
            />
          </div>
          <div className="flex justify-between text-sm">
            <span
              className={
                passwordStrength?.isStrongEnough
                  ? "text-green-500"
                  : "text-gray-500"
              }
            >
              {passwordStrength
                ? PASSWORD_STRENGTH_LEVELS[
                    passwordStrength.score as keyof typeof PASSWORD_STRENGTH_LEVELS
                  ]
                : ""}
            </span>
            {passwordStrength?.feedback && (
              <span className="text-gray-500">{passwordStrength.feedback}</span>
            )}
          </div>
        </div>
      )}

      {showConfirmation && onConfirmChange && (
        <div className="space-y-1">
          <Input
            value={confirmValue}
            type="password"
            onChange={(e) => onConfirmChange(e.target.value)}
            placeholder={confirmPlaceholder}
            className={`h-12 w-full ${
              showMismatchError ? "ring-2 ring-red-500" : ""
            } ${className}`}
          />
          {showMismatchError && (
            <div className="text-sm text-red-500">Passwords do not match</div>
          )}
          {passwordsMatch && confirmValue.length > 0 && (
            <div className="text-sm text-green-500">Passwords match</div>
          )}
        </div>
      )}
    </div>
  );
}
